import expandSessionsForFirebase from "./expandSessionsForFirebase";
import hasSessionTypeForFilter from "./hasSessionTypeForFilter";
import type { FirebaseAvailabilityPathMode } from "../../firebase/allAvailability";
import type { FirebaseAllAvailabilityById } from "../../model/firebaseAllAvailability";
import type { RestaurantDetailsById } from "../../model/restaurants/byId";
import type { ByRestaurantId } from "../../model/types";
import type { NonEmptyArray } from "../../types";

export type { FirebaseAvailabilityPathMode };

/**
 * Week uses session-level Firebase paths; specific dates keep per-date paths.
 */
export const getFirebaseAvailabilityPathMode = (
  date: string,
): FirebaseAvailabilityPathMode => (date === "week" ? "session" : "date");

const restaurantSupportsFirebaseSession = (
  restaurant: RestaurantDetailsById[number],
  session: SessionType,
  walkInEnabled: boolean,
): boolean => {
  if (restaurant == null) {
    return false;
  }

  return hasSessionTypeForFilter(
    walkInEnabled
      ? restaurant.walkInSessionTypes
      : restaurant.firstTableSessionTypes,
    session,
  );
};

export const buildFirebaseAllAvailabilitySessionsById = (
  restaurantsIds: number[],
  sessions: SessionType[],
  hasLastTable: boolean,
  restaurantsById: RestaurantDetailsById,
  walkInEnabled: boolean,
): ByRestaurantId<SessionType[]> => {
  const filterSessions = expandSessionsForFirebase(sessions, hasLastTable);

  return Object.fromEntries(
    restaurantsIds.map((restaurantId) => {
      const restaurant = restaurantsById[restaurantId];
      const sessionsForRestaurant = filterSessions.filter((session) =>
        restaurantSupportsFirebaseSession(restaurant, session, walkInEnabled),
      );

      return [restaurantId, sessionsForRestaurant];
    }),
  );
};

export const isDiscountVisible = (
  regularOnly: boolean,
  date: string,
  discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>,
): boolean =>
  !regularOnly &&
  (date === "week" ||
    Object.values(discountDatesToShowById).some((discountDatesToShow) =>
      discountDatesToShow?.includes(date),
    ));

export const filterIdsForDiscountVisibility = (
  ids: number[],
  discountVisible: boolean,
  date: string,
  discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>,
): number[] => {
  if (!discountVisible) {
    return [];
  }

  if (date === "week") {
    return ids;
  }

  return ids.filter((id) => discountDatesToShowById[id]?.includes(date));
};

export const getMissingFirebaseAllAvailabilityIds = (
  ids: number[],
  firebaseAllAvailabilityById: FirebaseAllAvailabilityById,
  sessions: SessionType[],
  date: string,
  hasLastTable: boolean,
  discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>,
  restaurantsById: RestaurantDetailsById,
  walkInEnabled: boolean,
): number[] =>
  ids.filter((id) => {
    const filterSessions =
      buildFirebaseAllAvailabilitySessionsById(
        [id],
        sessions,
        hasLastTable,
        restaurantsById,
        walkInEnabled,
      )[id] ?? [];

    if (filterSessions.length === 0) {
      return false;
    }

    const datesForRestaurant =
      date === "week" ? (discountDatesToShowById[id] ?? []) : [date];

    if (datesForRestaurant.length === 0) {
      return true;
    }

    return filterSessions.some((filterSession) => {
      const sessionAvailability =
        firebaseAllAvailabilityById[id]?.[filterSession];

      if (!sessionAvailability) {
        return true;
      }

      return datesForRestaurant.some(
        (discountDate) => sessionAvailability[discountDate] === undefined,
      );
    });
  });

export const buildDiscountDatesByIdForFetch = (
  ids: number[],
  discountDatesToShowById: ByRestaurantId<NonEmptyArray<string>>,
): ByRestaurantId<NonEmptyArray<string>> =>
  Object.fromEntries(
    ids.flatMap((id) => {
      const dates = discountDatesToShowById[id];
      return dates ? [[id, dates] as const] : [];
    }),
  );
